import React, { FC, Fragment, useState, useCallback } from "react";

import { FormControl, RadioGroup, InputLabel, makeStyles, createStyles } from "@material-ui/core";

import MshWalledIcon from "../../assets/images/msh_walled_icon.png";
import MshDoubleWallIcon from "../../assets/images/msh_double_wall_icon.png";
import MshWhiteTubIcon from "../../assets/images/msh_white_tub_icon.png";
import MshDoubleWallIsolationIcon from "../../assets/images/msh_double_wall_isolation_icon.png";

import { ChangeNames } from "../../clients/FirebaseClient/types";
import { useFormModel } from "../core/FormModelHelpes";
import { useStyles } from "../../useStyles";
import { FormControlLabel } from "../core/FormControlLabel";
import { Radio } from "../core/Radio";
import { NumberInput } from "../core/NumberField";

const useStylesComponent = makeStyles(() =>
    createStyles({
        componentGroup: {
            marginTop: 48,
        },
        componentGroupForTextfield: {
            marginTop: 48,
            width: "80%",
        },
        imageRadioElement: {
            display: "flex",
            flexDirection: "column",
            width: 244,
            marginTop: 24,
            marginRight: 24,
        },
        imageRadio: {
            width: 244,
            height: 195,
        },
    }),
);

export const NewConnectionMultilineCellarOuterWallForm: FC = () => {
    const componentClasses = useStylesComponent();
    const classes = useStyles();

    const { values, setValues, submitChange } = useFormModel<
        ChangeNames.NEW_CONNECTION_MULTILINE_CELLAR_OUTER_WALL
    >();

    const [doubleWall, setDoubleWall] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    const convertSealing = (): string => {
        const path = values.newConnectionMultilineCellarOuterWall.value.sealing.value;
        if (path === null) return "";
        if (path) return "yes";
        return "no";
    };

    const convertCharacteristics = useCallback((): string => {
        const path = values.newConnectionMultilineCellarOuterWall.value.characteristics.value;
        if (path.walled) return "walled";
        if (path.doubleWall) return "doubleWall";
        if (path.whiteTub) return "whiteTub";
        return "";
    }, [values.newConnectionMultilineCellarOuterWall.value.characteristics.value]);

    const convertDoubleWallSealingLayer = (): string => {
        const path =
            values.newConnectionMultilineCellarOuterWall.value.doubleWallSealingLayer.value;
        if (path && path.concrete) return "concrete";
        if (path && path.exteriorFormwork) return "exteriorFormwork";
        if (path && path.onOuterWall) return "onOuterWall";
        return "";
    };

    const convertDoubleWallInteriorInsulation = (): string => {
        const path =
            values.newConnectionMultilineCellarOuterWall.value.doubleWallInteriorInsulation.value;
        if (path === null) return "";
        if (path) return "yes";
        return "no";
    };

    const wallThickness = (
        <div>
            <InputLabel className={classes.labledTextfieldLabel}>
                Wandstärke - Richtwert 70 bis 600mm - (ohne Außendämmung) *
            </InputLabel>
            <NumberInput
                className={classes.textField}
                decimalScale={
                    values.newConnectionMultilineCellarOuterWall.value.wallThickness.value.precision
                }
                allowLeadingZeros={false}
                allowNegative={false}
                thousandSeparator="."
                decimalSeparator=","
                adornment={
                    values.newConnectionMultilineCellarOuterWall.value.wallThickness.value.unit
                }
                value={
                    values.newConnectionMultilineCellarOuterWall.value.wallThickness.value.amount ||
                    ""
                }
                placeholder="0"
                onValueChange={v =>
                    setValues(values => {
                        values.newConnectionMultilineCellarOuterWall.value.wallThickness.value.amount =
                            v.floatValue || 0;
                    })
                }
                error={
                    (values.newConnectionMultilineCellarOuterWall.error || "").indexOf(
                        "wallThickness",
                    ) !== -1
                }
            />
        </div>
    );

    const sealing = (
        <div className={componentClasses.componentGroup}>
            <h4 className={classes.h4} style={{ marginBottom: 6, marginTop: 0 }}>
                Abdichtung auf der Außenwand
            </h4>
            <p className={classes.p} style={{ marginBottom: 0, marginTop: 6 }}>
                Schwarze Wanne: Bitumendickbeschichtung, Abdichtbahnen
            </p>
            <FormControl component="fieldset" style={{ width: "100%", display: "flex" }}>
                <RadioGroup
                    value={convertSealing()}
                    onChange={(e, v) => {
                        const newValues = setValues(values => {
                            values.newConnectionMultilineCellarOuterWall.value.sealing.value =
                                v.indexOf("yes") >= 0;
                        });
                        submitChange(newValues);
                    }}
                >
                    <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
                        <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Ja"
                            style={{ marginRight: 150 }}
                            error={
                                (
                                    values.newConnectionMultilineCellarOuterWall.value.sealing
                                        .error || ""
                                ).indexOf("sealing:") !== -1
                            }
                        />
                        <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="Nein"
                            error={
                                (
                                    values.newConnectionMultilineCellarOuterWall.value.sealing
                                        .error || ""
                                ).indexOf("sealing:") !== -1
                            }
                        />
                    </div>
                </RadioGroup>
            </FormControl>
        </div>
    );

    const characteristics = useCallback(() => {
        type ImageRadioElementProps = {
            icon: string;
            value: string;
            label: string;
        };
        const ImageRadioElement: FC<ImageRadioElementProps> = ({
            icon,
            value,
            label,
        }: ImageRadioElementProps) => (
            <div className={componentClasses.imageRadioElement}>
                <img alt="" src={icon} className={componentClasses.imageRadio} />
                <FormControlLabel
                    value={value}
                    control={<Radio />}
                    label={label}
                    style={{ marginTop: 24 }}
                    error={
                        (
                            values.newConnectionMultilineCellarOuterWall.value.characteristics
                                .error || ""
                        ).indexOf("characteristics") !== -1
                    }
                />
            </div>
        );

        return (
            <div className={componentClasses.componentGroup}>
                <h4 className={classes.h4} style={{ marginBottom: 0, marginTop: 0 }}>
                    Welche Wandbeschaffenheit des Kellers liegt vor?
                </h4>
                <FormControl component="fieldset" style={{ width: "100%", display: "flex" }}>
                    <RadioGroup
                        value={convertCharacteristics()}
                        onChange={(e, v) => {
                            const newValues = setValues(values => {
                                const path =
                                    values.newConnectionMultilineCellarOuterWall.value
                                        .characteristics.value;
                                path.walled = v.indexOf("walled") >= 0;
                                path.doubleWall = v.indexOf("doubleWall") >= 0;
                                path.whiteTub = v.indexOf("whiteTub") >= 0;
                                setDoubleWall(v.indexOf("doubleWall") >= 0);

                                if (v.indexOf("doubleWall") === -1) {
                                    values.newConnectionMultilineCellarOuterWall.value.doubleWallSealingLayer.value = null;
                                    values.newConnectionMultilineCellarOuterWall.value.doubleWallInteriorInsulation.value = null;
                                    values.newConnectionMultilineCellarOuterWall.value.doubleWallInteriorInsulation.value = null;
                                }
                            });
                            submitChange(newValues);
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                            }}
                        >
                            <ImageRadioElement
                                icon={MshWalledIcon}
                                value="walled"
                                label="Gemauert"
                            />
                            <ImageRadioElement
                                icon={MshDoubleWallIcon}
                                value="doubleWall"
                                label="Doppel-/ Elementwand"
                            />
                            <ImageRadioElement
                                icon={MshWhiteTubIcon}
                                value="whiteTub"
                                label="Weiße Wanne / Vollwand aus WU-Beton"
                            />
                        </div>
                    </RadioGroup>
                </FormControl>
            </div>
        );
    }, [
        convertCharacteristics,
        setValues,
        classes.h4,
        componentClasses.imageRadio,
        componentClasses.componentGroup,
        componentClasses.imageRadioElement,
        submitChange,
        values.newConnectionMultilineCellarOuterWall.value.characteristics.error,
    ]);

    const doubleWallSealingLayer = (
        <div className={componentClasses.componentGroup}>
            <h4 className={classes.h4} style={{ marginBottom: 0, marginTop: 0 }}>
                Wo liegt die Abdichtebene?
            </h4>
            <FormControl component="fieldset" style={{ width: "100%", display: "flex" }}>
                <RadioGroup
                    value={convertDoubleWallSealingLayer()}
                    onChange={(e, v) => {
                        const newValues = setValues(values => {
                            values.newConnectionMultilineCellarOuterWall.value.doubleWallSealingLayer.value = {
                                concrete: v.indexOf("concrete") >= 0,
                                exteriorFormwork: v.indexOf("exteriorFormwork") >= 0,
                                onOuterWall: v.indexOf("onOuterWall") >= 0,
                            };
                        });
                        submitChange(newValues);
                    }}
                >
                    <FormControlLabel
                        value="concrete"
                        control={<Radio />}
                        label="Abdichtebene Ortbeton (WU-Beton)"
                        error={
                            (values.newConnectionMultilineCellarOuterWall.error || "").indexOf(
                                "doubleWallSealingLayer",
                            ) !== -1
                        }
                    />

                    <FormControlLabel
                        value="exteriorFormwork"
                        control={<Radio />}
                        label="Abdichtebene Schalung (Außen) ohne zusätzliche Abdichtung auf der Außenwand"
                        error={
                            (values.newConnectionMultilineCellarOuterWall.error || "").indexOf(
                                "doubleWallSealingLayer",
                            ) !== -1
                        }
                    />
                    <FormControlLabel
                        value="onOuterWall"
                        control={<Radio />}
                        label="Abdichtebene auf der Außenwand"
                        error={
                            (values.newConnectionMultilineCellarOuterWall.error || "").indexOf(
                                "doubleWallSealingLayer",
                            ) !== -1
                        }
                    />
                </RadioGroup>
            </FormControl>
        </div>
    );

    const doubleWallInteriorInsulation = (
        <div className={componentClasses.componentGroup}>
            <h4 className={classes.h4} style={{ marginBottom: 0, marginTop: 0 }}>
                Hat die Doppel-/Elementwand eine Innenliegende Isolierung?
            </h4>
            <FormControl component="fieldset" style={{ width: "100%", display: "flex" }}>
                <RadioGroup
                    value={convertDoubleWallInteriorInsulation()}
                    onChange={(e, v) => {
                        const newValues = setValues(values => {
                            values.newConnectionMultilineCellarOuterWall.value.doubleWallInteriorInsulation.value =
                                v.indexOf("yes") >= 0;
                        });
                        submitChange(newValues);
                    }}
                >
                    <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
                        <div className={componentClasses.imageRadioElement}>
                            <img
                                alt=""
                                src={MshDoubleWallIsolationIcon}
                                className={componentClasses.imageRadio}
                            />
                            <FormControlLabel
                                value="yes"
                                control={<Radio />}
                                label="Ja"
                                style={{ marginRight: 150 }}
                                error={
                                    (
                                        values.newConnectionMultilineCellarOuterWall.error || ""
                                    ).indexOf("doubleWallInteriorInsulation") !== -1
                                }
                            />
                        </div>
                        <div className={componentClasses.imageRadioElement}>
                            <img
                                alt=""
                                src={MshDoubleWallIcon}
                                className={componentClasses.imageRadio}
                            />
                            <FormControlLabel
                                value="no"
                                control={<Radio />}
                                label="Nein"
                                error={
                                    (
                                        values.newConnectionMultilineCellarOuterWall.error || ""
                                    ).indexOf("doubleWallInteriorInsulation") !== -1
                                }
                            />
                        </div>
                    </div>
                </RadioGroup>
            </FormControl>
        </div>
    );

    if (!isInitialized) {
        setDoubleWall(
            values.newConnectionMultilineCellarOuterWall.value.characteristics.value.doubleWall,
        );
        setIsInitialized(true);
    }

    return (
        <div className={classes.container}>
            <h4 className={classes.h4} style={{ marginTop: 40 }}>
                Beschaffenheit der Kellerwand
            </h4>
            {wallThickness}
            {sealing}
            {characteristics()}
            {doubleWall && (
                <Fragment>
                    {doubleWallSealingLayer}
                    {doubleWallInteriorInsulation}
                </Fragment>
            )}
        </div>
    );
};
